import { atom } from 'jotai';
import { atomFamily, atomWithDefault, loadable } from 'jotai/utils';
import { IIntegrationsAppDataModel } from '../../../data-models/integrations-app.data-model';
import {
  fetchAirtableEntities,
  fetchAllFDFields,
  fetchConnectedBases,
  fetchEstablishedSyncs,
  fetchIntegrations,
  fetchSyncColumns,
  fetchTablesForBase,
} from '../../../services/queries/MaggieIntegrationsQueries';
import {
  IAirTableBaseDataModel,
  IAirTableBasesResponseDataModel,
  IAirTableTablesResponseDataModel,
} from '../../../data-models/airtable-base.data-model';
import {
  IAirTableEntityDataModel,
  IForesightFieldDataModel,
} from '../../../data-models/airtable-entity.data-model';
import { getAllIndexColumnsForEntity } from '../Airtable/utils/syncUtils';
import {
  IBaseColumnDataModel,
  IEstablishedSyncDataModel,
  ISyncColumnsDataModel,
} from '../../../data-models/airtable-sync.data-model';
import { selectedEntityState, selectedTableState } from './ConfigurationState';

export const integrationsState = atomWithDefault<
  IIntegrationsAppDataModel[] | null | Promise<IIntegrationsAppDataModel[] | null>
>(() => fetchIntegrations());

export const integrationsStateLoadable = loadable(integrationsState);

export const airTableBasesState = atomWithDefault<
  IAirTableBasesResponseDataModel | null | Promise<IAirTableBasesResponseDataModel | null>
>(() => fetchConnectedBases());

export const airTableBasesStateLoadable = loadable(airTableBasesState);

export const airTableBasesMapState = atom<Promise<Map<string, IAirTableBaseDataModel> | null>>(
  async (get) => {
    const bases = (await get(airTableBasesState))?.bases;
    const map = new Map<string, IAirTableBaseDataModel>();
    if (bases) {
      bases.forEach((base) => {
        map.set(base.id, base);
      });
    }
    return map;
  }
);

export const airTableTablesState = atomFamily((baseId: string) =>
  atomWithDefault<IAirTableTablesResponseDataModel | null | Promise<IAirTableTablesResponseDataModel | null>>(
    () => {
      if (!baseId) {
        return null;
      }
      return fetchTablesForBase(baseId);
    }
  )
);

export const airTableTablesStateLoadable = atomFamily((baseId: string) =>
  loadable(airTableTablesState(baseId))
);

export const airtableEntitiesState = atomWithDefault<
  IAirTableEntityDataModel[] | null | Promise<IAirTableEntityDataModel[] | null>
>(() => fetchAirtableEntities());

export const airtableEntitiesStateLoadable = loadable(airtableEntitiesState);

export const entityMapState = atom<Promise<Map<string, IAirTableEntityDataModel>>>(async (get) => {
  const entities = await get(airtableEntitiesState);
  const map = new Map<string, IAirTableEntityDataModel>();
  if (entities) {
    entities.forEach((entity) => {
      map.set(entity.entityValue, entity);
    });
  }
  return map;
});

export const entityMapStateLoadable = loadable(entityMapState);

// each string element has entityValue,columnValue format
export const identifierFDColumnsState = atom<Promise<Set<string>>>(async (get) => {
  const identifierColsSet = new Set<string>();
  const entities = await get(airtableEntitiesState);
  if (entities) {
    entities.forEach((entity) => {
      entity.uniqueColumns.forEach((uniqueColumn) => {
        if (uniqueColumn.isSynced) {
          uniqueColumn.columns.forEach((column) => {
            identifierColsSet.add(`${entity.entityValue},${column.value}`);
          });
        }
      });
    });
  }
  return identifierColsSet;
});

export const identifierFDColumnsForSelectedEntityState = atom<Promise<Set<string>>>(async (get) => {
  const identifierColsSet = await get(identifierFDColumnsState);
  const selectedEntity = get(selectedEntityState)?.entityValue;
  if (!identifierColsSet || !selectedEntity) {
    return new Set();
  }
  return new Set(getAllIndexColumnsForEntity(identifierColsSet, selectedEntity));
});

/** columns for selected entity, base and table */
export const syncColumnsState = atom<Promise<ISyncColumnsDataModel | null>>(async (get) => {
  const baseId = get(selectedTableState)?.baseId;
  const tableId = get(selectedTableState)?.id;
  const entity = get(selectedEntityState)?.entityValue;
  if (!baseId || !tableId || !entity) {
    return null;
  }

  return await fetchSyncColumns({ baseId, tableId, entity });
});

export const syncColumnsStateLoadable = loadable(syncColumnsState);

/** columns map for selected entity, base and table */
export const foresightColumnsMapState = atom<Promise<Map<string, IBaseColumnDataModel>>>(async (get) => {
  const foresightColumns = (await get(syncColumnsState))?.foresightColumns;
  const map = new Map<string, IBaseColumnDataModel>();
  if (foresightColumns) {
    foresightColumns.forEach((column) => {
      map.set(column.value, column);
    });
  }
  return map;
});

export const airtableColumnsMapState = atom<Promise<Map<string, IBaseColumnDataModel>>>(async (get) => {
  const airtableColumns = (await get(syncColumnsState))?.airtableColumns;
  const map = new Map<string, IBaseColumnDataModel>();
  if (airtableColumns) {
    airtableColumns.forEach((column) => {
      map.set(column.value, column);
    });
  }
  return map;
});

export const establishedSyncsState = atomWithDefault<
  IEstablishedSyncDataModel[] | Promise<IEstablishedSyncDataModel[]>
>(() => fetchEstablishedSyncs());

export const establishedSyncsStateLoadable = loadable(establishedSyncsState);

export const allForesightFieldsState = atomWithDefault<
  IForesightFieldDataModel[] | Promise<IForesightFieldDataModel[]>
>(() => fetchAllFDFields());

export const allFDFieldsMapState = atom<Promise<Map<string | number, IForesightFieldDataModel>>>(
  async (get) => {
    const allSyncColumns = await get(allForesightFieldsState);
    const map = new Map<string, IForesightFieldDataModel>();
    if (allSyncColumns) {
      allSyncColumns.forEach((column) => {
        // for custom fields, the id is used as the entityColumn value for sync
        // for other fields, the entityField is used as the entityColumn value for sync
        if (column.id) {
          map.set(String(column.id), column);
        } else {
          map.set(column.entityField, column);
        }
      });
    }
    return map;
  }
);

export const allFDFieldsMapStateLoadable = loadable(allFDFieldsMapState);
